import type { Writable } from "svelte/store";
import type { FichierConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// 1Fichier does not answer other origins. Calls go through /api/fichier, which
// talks to https://api.1fichier.com. The API key is a request header.

export const prop_fichier_url = "fichier_url";
export const prop_fichier_mime = "fichier_mime";
export const prop_fichier_whirlpool = "fichier_whirlpool";

class FileSystemFichier extends FS {
  supportsDirRename = true;

  constructor(config: FichierConfig) {
    super(config);
  }

  get apiKey(): string {
    return (this.config as FichierConfig).apiKey;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await fichierCall(this.apiKey, mkURL("/api/fichier/list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("1fichier list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the API key. Fetch the file
    // and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/fichier/download", "path", path);
    let xhr = await fichierSend("GET", url, this.apiKey, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  // 1Fichier does not store empty files: the server refuses them.
  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = mkURL("/api/fichier/upload", "path", path);
    await fichierSend("PUT", url, this.apiKey, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = mkURL("/api/fichier/rename", "path", path, "newPath", newPath);
    await fichierCall(this.apiKey, url, "POST");
  }

  async createDirectory(dirPath: string): Promise<void> {
    await fichierCall(this.apiKey, mkURL("/api/fichier/mkdir", "path", dirPath), "POST");
  }

  // There is no trash. A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    await fichierCall(this.apiKey, mkURL("/api/fichier/delete", "path", path), "POST");
  }
}

export async function testFichierConnection(apiKey: string): Promise<void> {
  await fichierCall(apiKey, "/api/fichier/test");
}

async function fichierCall(apiKey: string, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, {
    method,
    headers: { "X-Fichier-Key": apiKey },
  });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `1fichier ${rsp.status}`);
  }
  return js;
}

function fichierSend(
  method: string,
  url: string,
  apiKey: string,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    xhr.setRequestHeader("X-Fichier-Key", apiKey);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      fichierErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`1fichier ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("1fichier request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function fichierErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `1fichier ${xhr.status}`;
}

export function makeFileSystemFichier(config: FichierConfig): FileSystemFichier {
  return new FileSystemFichier(config);
}
